// Les sites n'ont pas de champ « enseigne » : on la déduit du préfixe du nom
// (« ED - Fréjus », « ELECTRO DEPOT TOULON », « Boul La Valentine »…).
const BRAND_PREFIXES: { brand: string; prefix: RegExp; keepFullName?: boolean }[] = [
  { brand: "Electro Dépôt", prefix: /^(?:electro\s*d[eé]p[oô]t\b|ed\b)\s*-?\s*/i },
  { brand: "Auchan", prefix: /^auchan\b\s*-?\s*/i },
  { brand: "Boulanger", prefix: /^boul(?:anger)?\b\s*-?\s*/i },
  { brand: "Monoprix", prefix: /^monop(?:rix)?\b\s*-?\s*/i },
  { brand: "Lagoona", prefix: /^lagoona\b/i, keepFullName: true },
];

export const OTHER_BRAND = "Autres";

type NamedSite = { id: string; name: string };

export type SiteBrandGroup<T extends NamedSite> = {
  brand: string;
  sites: { site: T; label: string }[];
};

export function getSiteBrand(name: string): { brand: string; label: string } {
  const trimmed = name.trim();
  for (const { brand, prefix, keepFullName } of BRAND_PREFIXES) {
    const match = trimmed.match(prefix);
    if (!match) continue;
    const rest = trimmed.slice(match[0].length).trim();
    return { brand, label: keepFullName || !rest ? trimmed : rest };
  }
  return { brand: OTHER_BRAND, label: trimmed };
}

// Enseignes les plus fournies en premier, « Autres » toujours en dernier.
export function groupSitesByBrand<T extends NamedSite>(sites: T[]): SiteBrandGroup<T>[] {
  const groups = new Map<string, SiteBrandGroup<T>>();
  for (const site of sites) {
    const { brand, label } = getSiteBrand(site.name ?? "");
    if (!groups.has(brand)) groups.set(brand, { brand, sites: [] });
    groups.get(brand)!.sites.push({ site, label });
  }

  const compare = (a: string, b: string) => a.localeCompare(b, "fr", { sensitivity: "base" });
  for (const group of groups.values()) {
    group.sites.sort((a, b) => compare(a.label, b.label));
  }

  return [...groups.values()].sort((a, b) => {
    if (a.brand === OTHER_BRAND) return 1;
    if (b.brand === OTHER_BRAND) return -1;
    return b.sites.length - a.sites.length || compare(a.brand, b.brand);
  });
}

// « LE MANS » → « Le Mans », « PLAN DE CAMPAGNE » → « Plan de Campagne » ;
// les prépositions restent en minuscules sauf en tête (les articles font partie des noms de lieux),
// les sigles courts (SAV) en capitales. Un nom déjà en casse mixte est gardé tel quel.
const ACRONYMS = new Set(["SAV", "ZAC", "CC"]);
const SMALL_WORDS = new Set(["de", "du", "des", "sur", "sous", "en", "et", "aux", "au", "à", "d"]);
export function toTitleCase(text: string): string {
  if (text !== text.toUpperCase()) return text;
  let index = 0;
  return text.toLowerCase().replace(/[\p{L}]+/gu, (word) => {
    const isFirst = index++ === 0;
    if (ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
    if (!isFirst && SMALL_WORDS.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

// Nom du magasin pour les clients : « ED - Avignon » → « Electro Dépôt Avignon ».
export function getStoreDisplayName(siteName: string): string {
  const name = siteName.trim();
  const { brand, label } = getSiteBrand(name);
  if (brand === OTHER_BRAND || label === name) return toTitleCase(name);
  return `${brand} ${toTitleCase(label)}`;
}
